import { MultiSelectOption, TooltipContent } from '@dxo/ng-swift-ui';
import { ClaimStatusEnum, SearchClaimData, SearchSortDirection, SymetraDateTime } from '@benefitsui/app/api/benefits-clients';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';
import {
  ClaimLeaveTypeEnum,
  ClientClaimFieldEnum,
  ClientClaimTypeEnum,
  clientClaimTypeFromClaimType,
  StatusColorEnum
} from './claim-data-enums';
import { planCodesToClaimTypeMapping } from './plan-code-mapping';
import { DateTimeUtilities } from '@benefitsui/app/shared/utilities/dateTimeUtilities';
import { Constants } from '@benefitsui/app/shared/constants/constants';
import { IClaimInfo, IKeyDates, IClaimBenefit, IClaimPayment, IProgressTracker } from './claim-details.model';

export interface IClaimData {
  benefits?: IClaimBenefit[];
  claimType: IClaimTypeUi;
  claimDetails: IClaimDetails;
  claimInfo?: IClaimInfo;
  division: IDivision;
  employee: IClaimEmployeeData;
  keyDates?: IKeyDates;
  leaveType?: ClaimLeaveTypeEnum;
  payments?: IClaimPayment[];
  progressTracker?: IProgressTracker;
  received: SymetraDateTime;
  relatedClaims: IClaimData[];
  started: IClaimDateInfo;
  status: ClaimStatusEnum;
  wop?: boolean; // Waiver of Premium flag for Life insurance claims
}

export interface IClaimDateInfo {
  claimDate: SymetraDateTime;
}

export interface IClaimDetails {
  claimId: string;
  claimDescription: string;
}

export interface IClaimEmployeeData {
  firstName: string;
  lastName: string;
  birthdate: SymetraDateTime;
  // Additional fields for claim detail scenarios
  ssn?: string;
  gender?: string;
  workState?: string;
  caseManager?: string;
}

export interface IClaimSearchParameters {
  filters: IFilters;
  page: number;
  pageSize: number;
  searchText: string;
  sortField: ClientClaimFieldEnum;
  sortDirection: SearchSortDirection;
}

export interface IClaimTableItem {
  claimId: string;
  claimType: string;
  division: string;
  leaveType: string;
  name: string;
  received: string;
  started: string;
  status: string;
  statusColor: StatusColorEnum;
}

export interface IClaimTypeUi {
  claimTypeLabel: string;
  claimTypes: ClientClaimTypeEnum[];
  planCodesSet: string[];
}

export interface IDetailsPageLabels {
  buttonLabel: string;
  buttonUrl: string;
  cardBody: string;
}

export interface IDivision {
  name: string;
  number: string;
}

export interface IFilters {
  claimTypes: MultiSelectOption[];
}

export interface IPaginatedClaimsData {
  claims: IClaimData[];
  totalResults: number;
  currentPage: number;
  resultsPerPage: number;
}

export interface ITableHeader {
  text: string;
  key: string;
  sortable?: boolean;
  tooltipContent?: TooltipContent;
  icon?: string;
}

export const claimDataToClaimTableItem = (claimData: IClaimData): IClaimTableItem => {
  const claimTableItem: IClaimTableItem = {
    claimType: claimData.claimType.claimTypeLabel,
    claimId: claimData.claimDetails.claimId,
    division: claimData.division.name,
    leaveType: claimData.leaveType || Constants.NOT_APPLICABLE,
    name: `${claimData.employee.lastName}, ${claimData.employee.firstName}`,
    received: formatDate(claimData.received),
    started: formatDate(claimData.started.claimDate),
    status: claimData.status,
    statusColor: getStatusIndicator(claimData.status)
  };

  return claimTableItem;
};

export const createClaimTypeLabel = (claimTypes: ClientClaimTypeEnum[]): string => {
  const claimTypesSorted = claimTypes.sort((a, b) => a.localeCompare(b));
  const claimTypeLabel = claimTypesSorted.map((claimType) => claimType.toString()).join(Defaults.separator);

  return claimTypeLabel;
}

/**
 * WOP (Waiver of Premium) flag
 * If claimInfo.claimType is "Life" and wop is true, displays "Waiver of Premium"
 * Otherwise falls back to the original claim type logic
 */
export const getClaimTypeDisplayUi = (claimInfo: IClaimInfo | undefined, wop: boolean, planCodes: string[]): IClaimTypeUi => {
  // Check if this is a Life claim with WOP enabled
  if (claimInfo?.claimType === ClientClaimTypeEnum.Life && wop === true) {
    return {
      claimTypeLabel: ClientClaimTypeEnum.WaiverOfPremium,
      claimTypes: [ClientClaimTypeEnum.WaiverOfPremium],
      planCodesSet: planCodes
    };
  }
  
  // Fall back to original logic for all other cases
  return planCodesToClaimTypeUi(planCodes);
};

export const getStatusIndicator = (status: ClaimStatusEnum): StatusColorEnum => {
  switch (status) {
    case ClaimStatusEnum.Open:
      return StatusColorEnum.Green;
    case ClaimStatusEnum.Pending:
      return StatusColorEnum.Blue;
    case ClaimStatusEnum.Closed:
      return StatusColorEnum.Grey;

      case ClaimStatusEnum.Incomplete:
    default:
      return StatusColorEnum.Grey;
  }
};

export const planCodesToClaimTypeUi = (planCodes: string[]): IClaimTypeUi => {
  const planCodesSet = new Set(planCodes);
  const claimTypesSorted: ClientClaimTypeEnum[] = planCodesToClaimTypeMapping(planCodesSet);

  const claimTypeLabel = createClaimTypeLabel(claimTypesSorted);

  const claimTypeUi = {
    claimTypeLabel: claimTypeLabel,
    claimTypes: claimTypesSorted,
    planCodesSet: Array.from(planCodesSet)
  };

  return claimTypeUi;
};

export const searchClaimDataToClaimTableItem = (searchClaimData: SearchClaimData): IClaimTableItem => {
  const nameFormatted = `${searchClaimData.employee.lastName}, ${searchClaimData.employee.firstName}`;

  const clientClaimTypes = searchClaimData.claimTypes.map(
    (claimType) => clientClaimTypeFromClaimType(claimType)
  );

  const claimTypeLabel = createClaimTypeLabel(clientClaimTypes);

  const claimTableItem: IClaimTableItem = {
    claimType: claimTypeLabel,
    claimId: searchClaimData.claimNumber,
    division: searchClaimData.divisionInfo.divisionName,
    leaveType: Constants.NOT_APPLICABLE,
    name: nameFormatted,
    received: formatDate(searchClaimData.dateReceived),
    started: formatDate(searchClaimData.dateStarted),
    status: searchClaimData.status,
    statusColor: getStatusIndicator(searchClaimData.status)
  };

  return claimTableItem;
}

const formatDate = (date: SymetraDateTime | undefined | null): string => {
  if (!date) {
    return Defaults.stringEmpty;
  }

  const dateString = DateTimeUtilities.symetraDateTimeToString(date, DateTimeUtilities.MonthDayYearFormat);
  return dateString;
}
